import {
  useState,
  useEffect,
  useRef,
  FormEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useRouter, useLocation } from '@tanstack/react-router';
import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import InstapaperPremiumModal from './InstapaperPremiumModal';
import Icon from './ui/Icon';
import Tooltip from './ui/Tooltip';

interface CollapsibleSearchBarProps {
  isSubscriber?: boolean;
  initialQuery?: string;
  isBatchMode?: boolean;
}

function CollapsibleSearchBar({
  isSubscriber = false,
  initialQuery = '',
  isBatchMode = false,
}: CollapsibleSearchBarProps) {
  const router = useRouter();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the search query from the URL if we're on the search page
  // location.search is a typed object from TanStack Router, so we access the q property directly
  const urlQuery =
    location.pathname === '/search' &&
    location.search &&
    typeof location.search === 'object' &&
    'q' in location.search
      ? (location.search.q as string) || ''
      : '';
  const currentQuery = urlQuery || initialQuery || '';

  const [query, setQuery] = useState(currentQuery);
  // Initialize expanded state based on the current query. If we're on the search page with a query, start expanded.
  const [isExpanded, setIsExpanded] = useState(!!currentQuery);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const isAllowedToCollapse =
    isExpanded && !query.trim() && location.pathname !== '/search';

  // Update query when route changes
  useEffect(() => {
    if (
      location.pathname === '/search' &&
      location.search &&
      typeof location.search === 'object' &&
      'q' in location.search
    ) {
      const searchQuery = (location.search.q as string) || '';
      setQuery(searchQuery);

      // If we're on the search page with a query, keep it expanded
      if (searchQuery) {
        setIsExpanded(true);
      }
    }
  }, [location]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't collapse if clicking inside the search container or if there's a query
      if (!target.closest('[data-search-container]') && isAllowedToCollapse) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAllowedToCollapse]);

  // Handle escape key to collapse
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAllowedToCollapse) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAllowedToCollapse]);

  const handleSubmit = (e: FormEvent | ReactMouseEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      // Navigate back home if the user clears the search query
      router.navigate({
        to: '/home',
      });
      return;
    }

    // TODO(Drew subscription): Re-enable subscription check after testing
    if (!isSubscriber) {
      setShowPremiumModal(true);
      return;
    }

    router.navigate({
      to: '/search',
      search: {
        q: query,
        site: '',
        author: '',
        sort: 'relevance' as const,
        page: 1,
      },
    });
  };

  const handleIconClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const isMovingWithinContainer = relatedTarget?.closest('[data-search-container]');

    // Don't collapse if we're tabbing from the input to the icon or vice versa
    if (!isMovingWithinContainer && isAllowedToCollapse) {
      setIsExpanded(false);
    }
  };

  return (
    <div
      className={clsx(
        'transition-all duration-100 ease-out',
        // Don't collapse the search bar if we have an active search
        isBatchMode && !isExpanded
          ? 'pointer-events-none -translate-x-[22px] opacity-0'
          : 'translate-x-0 opacity-100',
      )}
    >
      <div data-search-container className="relative flex items-center">
        {/* Expanded Search Input - positioned so icon appears inside */}
        <div
          className={clsx(
            'absolute right-[-10px] flex items-center overflow-hidden transition-all duration-200 ease-out',
            isExpanded ? 'w-80 opacity-100' : 'w-0 opacity-0',
          )}
        >
          <form onSubmit={handleSubmit} className="w-full">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onBlur={handleBlur}
                placeholder={t`Search articles, tags, or content`}
                className="form-input bg-surface-secondary h-8 w-full py-1 pr-12 pl-3 text-sm placeholder-gray-500"
                data-testid="search-input"
                tabIndex={isExpanded ? 0 : -1}
                // Don't allow changing the search query if we have bookmark selections,
                // since it can be confusing to find them again to unselect
                disabled={isBatchMode}
              />
            </div>
          </form>
        </div>

        {/* Search Icon Button - always visible, acts as submit when expanded */}
        <button
          type="button"
          onClick={isExpanded ? handleSubmit : handleIconClick}
          onBlur={handleBlur}
          className={clsx(
            'button-icon z-medium relative transition-all',
            isExpanded && 'p-0 hover:bg-transparent',
          )}
          aria-label={t`Search`}
          disabled={isBatchMode}
        >
          <Tooltip content={t`Search`}>
            <Icon name="search" />
          </Tooltip>
        </button>
      </div>

      {/* Premium Modal */}
      <InstapaperPremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
      />
    </div>
  );
}

export default CollapsibleSearchBar;
